import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { SUPPORT_EMAIL } from '@/config/brand';
import { Loader2, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { subscriptionService } from '@/services/api/subscription.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import type { BillingInterval, SubscriptionPlan, StripePrice } from '@/types/subscription';
import { hasLiveSubscription } from '@/features/billing/billingStatus';
import { formatCurrency, type Currency } from '@/utils/currency';
import { isPrerendering, STATIC_PLANS } from '@/marketing/plans';

/**
 * The ad offer on the plan cards (the pricing page passes it only while the offer is active; nothing here is read otherwise): every price is shown
 * struck out next to the price after the discount, and the 14-day trial as 14 struck out and 30 days free. The numbers are computed, the words are given.
 */
export interface PricingOffer {
  /** The price after the offer, in cents, from the price in cents the card shows. */
  discount: (cents: number) => number;
  copy: { discountLabel: string; priceWas: string; trialWas: string; trialNow: string; trialSr: string; noCard: string };
}

interface PricingTableProps {
  currency?: string;
  /** The interval shown first. Annual is one click away. */
  interval?: BillingInterval;
  className?: string;
  /**
   * When true, the primary CTA always routes to `/register` regardless of auth state
   * and never opens Stripe Checkout. Use this for landing/marketing pages.
   */
  signupMode?: boolean;
  /** Where the plan buttons of a signed-out visitor go. The pricing page changes it only while the ad offer is active (it carries the offer id). */
  registerHref?: string;
  /** The ad offer: shown on the cards of a signed-out visitor (a signed-in owner goes to checkout, which does not apply it). */
  offer?: PricingOffer;
  /** Keeps every plan button disabled, e.g. while the billing status catches up with a payment that just went through. */
  holdActions?: boolean;
  /** The level of each plan's name (default 3, under a section heading; the pricing page has no section heading, so it passes 2; the tag stays h3 so the look does not change). */
  headingLevel?: 2 | 3;
}

/** The parts of an axios error body this table reads. A 422 `too_many_teammates` carries its counts inside `errors`. */
type ApiError = {
  response?: {
    status?: number;
    data?: { error?: string; errors?: { type?: string; seats_used?: number; seat_limit?: number } };
  };
};

/** `seats_used`/`seat_limit` of a 422 `too_many_teammates`, or null for any other error. */
function tooManyTeammates(error: unknown): { seatsUsed: number; seatLimit: number } | null {
  const response = (error as ApiError)?.response;
  const data = response?.data;
  if (response?.status !== 422 || data?.errors?.type !== 'too_many_teammates') return null;
  return {
    seatsUsed: Number(data.errors.seats_used),
    seatLimit: Number(data.errors.seat_limit),
  };
}

/** The translation key (under `plans:errors`) for a failed checkout or plan change. */
function failureKey(error: unknown): 'alreadyOnPlan' | 'unknownPlan' | 'noBillingAccount' | 'alreadySubscribed' | 'adminRequired' | 'network' | 'generic' {
  const response = (error as ApiError)?.response;
  if (!response) return 'network';
  if (response.status === 403) return 'adminRequired';
  switch (response.data?.error) {
    case 'Already on this plan': return 'alreadyOnPlan';
    case 'Unknown plan': return 'unknownPlan';
    case 'No billing account': return 'noBillingAccount';
    // checkout refuses while a live subscription exists (also set to cancel at period end); the answer is a refresh, never an automatic change_plan
    case 'Already subscribed':
    case 'Subscription already exists': return 'alreadySubscribed';
    default: return 'generic';
  }
}

const isNoSubscription = (error: unknown) => {
  const response = (error as ApiError)?.response;
  return response?.status === 422 && response.data?.error === 'No active subscription';
};

export function PricingTable({
  currency = 'usd',
  interval: initialInterval = 'month',
  className = '',
  signupMode = false,
  registerHref = '/register',
  offer,
  headingLevel = 3,
  holdActions = false,
}: PricingTableProps) {
  const { i18n, t } = useTranslation(['plans', 'subscription']);
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [selectedInterval, setSelectedInterval] = useState<BillingInterval>(initialInterval);
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  // plan code -> teammates to remove, from a 422 on that card
  const [blockedPlans, setBlockedPlans] = useState<Record<string, number>>({});
  // plan code -> translation key of the error shown on that card
  const [failures, setFailures] = useState<Record<string, ReturnType<typeof failureKey>>>({});

  // Names, descriptions and features come from translations by plan code, falling back to the API's own text
  const getPlanTranslation = (plan: SubscriptionPlan, planCode: string) => {
    const features = t(`plans:${planCode}.features`, { returnObjects: true, defaultValue: plan.marketingFeatures });
    return {
      name: t(`plans:${planCode}.name`, { defaultValue: plan.name }),
      description: t(`plans:${planCode}.description`, { defaultValue: plan.description }),
      seats: t(`plans:${planCode}.seats`, { defaultValue: '' }),
      features: Array.isArray(features) ? (features as string[]) : [],
    };
  };

  // Fetch all plans
  const { data: plans, isLoading, isError: plansFailed, refetch: refetchPlans } = useQuery({
    queryKey: ['subscription', 'plans'],
    // At build time (prerender) there is no API: the page is written with the static plans. In the browser the static plans stand in
    // while the live catalog loads, so the first paint is the same page and the numbers never jump.
    queryFn: () => (isPrerendering() ? Promise.resolve(STATIC_PLANS) : subscriptionService.getStripePlans()),
    placeholderData: signupMode || !isAuthenticated ? STATIC_PLANS : undefined,
  });

  // In the app: the current plan, the seats in use, and whether a plan change or a checkout applies
  const appMode = isAuthenticated && !signupMode;
  const { data: status, isSuccess: statusKnown, isError: statusFailed, refetch: refetchStatus } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: () => subscriptionService.getBillingStatus(),
    enabled: appMode,
  });
  // Only the owner pays. Staff and crew can see the plans, never buy.
  const isOwner = !user || user.role === 'admin';
  // Until the status is known, "not subscribed" is a guess, and a guess here can mean a second checkout
  const actionsHeld = appMode && (!statusKnown || holdActions);

  const subscribed = hasLiveSubscription(status);

  const checkout = async (planCode: string) => {
    const url = await subscriptionService.createCheckoutSession(planCode, selectedInterval, i18n.language, currency);
    window.location.assign(url);
  };

  // the offer is for sign-up: with a signed-in owner the buttons open checkout (or change the plan), so no offer is shown there
  const offerShown = offer && (signupMode || !isAuthenticated) ? offer : undefined;

  const handleChoosePlan = async (planCode: string) => {
    // Landing pages and visitors: always go to the sign-up form, never open Stripe
    if (signupMode || !isAuthenticated) {
      setLocation(registerHref);
      return;
    }

    if (actionsHeld || !isOwner) return;

    setPendingPlan(planCode);
    setFailures((current) => {
      const next = { ...current };
      delete next[planCode];
      return next;
    });
    setBlockedPlans((current) => {
      const next = { ...current };
      delete next[planCode];
      return next;
    });
    try {
      if (subscribed) {
        try {
          await subscriptionService.changePlan(planCode, selectedInterval);
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['billing', 'status'] }),
            queryClient.invalidateQueries({ queryKey: ['subscription'] }),
          ]);
          toast({ title: t('plans:success.planChanged', { plan: t(`plans:${planCode}.name`, { defaultValue: planCode }) }) });
          setPendingPlan(null);
          return;
        } catch (error) {
          // the subscription ended since the status was read: start a new one
          if (!isNoSubscription(error)) throw error;
        }
      }
      await checkout(planCode);
    } catch (error) {
      const blocked = tooManyTeammates(error);
      if (blocked) {
        setBlockedPlans((current) => ({ ...current, [planCode]: Math.max(blocked.seatsUsed - blocked.seatLimit, 1) }));
      } else {
        console.error('Failed to change plan or create checkout session:', error);
        const key = failureKey(error);
        setFailures((current) => ({ ...current, [planCode]: key }));
        // the subscription exists after all: read the status again
        if (key === 'alreadySubscribed') {
          // `current` syncs from Stripe, so read it before the status; the card then switches to Change plan
          void queryClient
            .refetchQueries({ queryKey: ['subscription'] })
            .catch(() => undefined)
            .then(() => refetchStatus());
        }
      }
      setPendingPlan(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (plansFailed || !plans || plans.length === 0) {
    return (
      <div className={`text-center py-12 space-y-4 ${className}`} role="status">
        <p className="text-gray-600">{plansFailed ? t('plans:pricing.loadFailed') : t('plans:pricing.unavailable')}</p>
        {plansFailed && (
          <Button variant="outline" onClick={() => void refetchPlans()}>
            {t('plans:pricing.retry')}
          </Button>
        )}
        {appMode && status?.read_only && (
          <p>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline text-gray-700">
              {t('plans:pricing.contactSupport')}
            </a>
          </p>
        )}
        {/* Signing up does not need the catalog */}
        {!appMode && (
          <Button onClick={() => setLocation(registerHref)}>{t('plans:buttons.startFreeTrial')}</Button>
        )}
      </div>
    );
  }

  // Helper to get price for selected interval and currency
  const getPriceForInterval = (plan: SubscriptionPlan, interval: BillingInterval): StripePrice | null => {
    // First try to find a price matching the requested currency
    const exactMatch = plan.prices.find(
      p => p.currency === currency &&
        p.recurring?.interval === interval &&
        p.type === 'recurring'
    );
    if (exactMatch) return exactMatch;

    // Fallback: return the first price matching the interval regardless of currency
    return plan.prices.find(
      p => p.recurring?.interval === interval &&
        p.type === 'recurring'
    ) || null;
  };

  // Helper to check if in test mode
  const isTestMode = !import.meta.env.PROD || import.meta.env.VITE_STRIPE_MODE === 'test';

  // Sort plans by price (lowest first) based on selected interval
  const sortedPlans = [...plans].sort((a, b) => {
    const priceA = getPriceForInterval(a, selectedInterval);
    const priceB = getPriceForInterval(b, selectedInterval);

    if (!priceA) return 1;
    if (!priceB) return -1;

    return priceA.unitAmount - priceB.unitAmount;
  });

  const intervalButton = (value: BillingInterval, label: string, badge?: string) => (
    <button
      type="button"
      aria-pressed={selectedInterval === value}
      onClick={() => setSelectedInterval(value)}
      className={`px-4 sm:px-6 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-2 ${selectedInterval === value
          ? 'bg-white text-gray-900 shadow-sm'
          : 'text-gray-600 hover:text-gray-900'
        }`}
    >
      {label}
      {badge && (
        <Badge variant="secondary" className="text-xs whitespace-nowrap bg-green-100 text-green-700 border-0">
          {badge}
        </Badge>
      )}
    </button>
  );

  return (
    <div className={`space-y-6 ${className}`}>
      {plans.some(plan => plan.metadata?.sandbox_billing === 'true') && (
        <p className="text-center text-sm text-gray-600" role="status">
          Stripe sandbox: test payments only. No real charges.
        </p>
      )}
      {appMode && statusFailed && (
        <div role="alert" className="flex flex-wrap items-center justify-center gap-3 text-sm text-red-700">
          <span>{t('plans:pricing.statusFailed')}</span>
          <Button size="sm" variant="outline" onClick={() => void refetchStatus()}>
            {t('plans:pricing.retry')}
          </Button>
        </div>
      )}
      {appMode && holdActions && (
        <p role="status" className="text-center text-sm text-gray-600">{t('plans:pricing.updating')}</p>
      )}
      {/* Interval Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex rounded-lg bg-gray-100 p-1">
          {intervalButton('month', t('plans:pricing.monthly'))}
          {intervalButton('year', t('plans:pricing.annual'), t('plans:pricing.twoMonthsFree'))}
        </div>
      </div>

      {/* Plans Grid */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${sortedPlans.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6`}>
        {sortedPlans.map((plan) => {
          const price = getPriceForInterval(plan, selectedInterval);
          if (!price) return null;

          const planCode = plan.metadata?.plan_code || plan.id;
          const planTranslation = getPlanTranslation(plan, planCode);
          const isCurrentPlan = subscribed && status?.plan.code === planCode && status?.interval === selectedInterval;
          const isPending = pendingPlan === planCode;
          const recommended = plan.metadata?.recommended === 'true';
          const sandboxBilling = plan.metadata?.sandbox_billing === 'true';

          // Too many teammates for this plan: known from the status before a click (the button is disabled),
          // or from a 422 after one (the button stays, so the owner can retry after removing teammates)
          const maxUsers = plan.limits.maxUsers;
          const tooSmall = !!status && maxUsers !== null && status.seats_used > maxUsers;
          const overBy = tooSmall ? status.seats_used - maxUsers : blockedPlans[planCode];
          const showSeatMessage = overBy !== undefined && !signupMode && isAuthenticated;

          const fullPrice = formatCurrency(price.unitAmount / 100, price.currency.toUpperCase() as Currency);
          const offerPrice = offerShown
            ? formatCurrency(offerShown.discount(price.unitAmount) / 100, price.currency.toUpperCase() as Currency)
            : undefined;

          const ctaLabel = signupMode || !isAuthenticated
            ? t('plans:buttons.startFreeTrial')
            : subscribed
              ? t('plans:buttons.switchPlan')
              : sandboxBilling ? 'Start sandbox checkout' : t('plans:buttons.subscribe');

          return (
            <Card
              key={planCode}
              data-plan-card={planCode}
              className={`relative flex flex-col bg-gray-50 border-0 ${isCurrentPlan ? 'ring-2 ring-primary' : ''
                }`}
            >
              <CardHeader className="space-y-3 pb-6">
                <div>
                  {/* The badges sit in the title row, on the right, so a recommended card is not taller than its neighbours */}
                  <div className="flex items-center justify-between gap-2">
                    {/* an h3 by tag (the styles are by tag), its level by aria-level: the pricing page has no section heading above the plans */}
                    <h3 aria-level={headingLevel} className={cn('text-h2 text-ink', 'text-xl font-bold')}>
                      {planTranslation.name}
                    </h3>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {recommended && (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-xs font-semibold" data-testid="plan-recommended-badge">
                          {t('plans:pricing.recommended')}
                        </Badge>
                      )}
                      {(isTestMode || sandboxBilling) && (
                        <Badge className="bg-yellow-500 text-black text-xs font-semibold">
                          TEST MODE
                        </Badge>
                      )}
                      {isCurrentPlan && (
                        <Badge variant="default" className="text-xs">
                          {t('plans:pricing.currentPlan')}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardDescription className="text-sm text-gray-600 mt-1">
                    {planTranslation.description}
                  </CardDescription>
                  {planTranslation.seats && (
                    <p className="text-sm font-medium text-gray-900 mt-2">
                      {planTranslation.seats}
                    </p>
                  )}
                </div>
              </CardHeader>

              <CardContent className="flex-1 space-y-6">
                {/* Pricing: the amount Stripe charges per interval */}
                <div className={offerShown ? 'flex items-baseline gap-x-2 gap-y-1 flex-wrap' : 'flex items-baseline gap-1 flex-wrap'}>
                  {offerShown && (
                    <s data-offer-was className="text-xl font-medium text-gray-500">
                      <span className="sr-only">{offerShown.copy.priceWas} </span>
                      {fullPrice}
                    </s>
                  )}
                  <span className="text-4xl font-bold text-gray-900">{offerPrice ?? fullPrice}</span>
                  <span className="text-gray-600 text-sm">
                    {selectedInterval === 'year' ? t('plans:pricing.perYear') : t('plans:pricing.perMonth')}
                  </span>
                </div>

                {offerShown && (
                  <div data-offer-card className="-mt-3 space-y-1">
                    <p className="text-sm font-semibold text-green-700">{offerShown.copy.discountLabel}</p>
                    {/* the strike is a look: screen readers get the sentence instead of "14 30 days free" */}
                    <p className="text-base font-semibold text-gray-900">
                      <span aria-hidden="true">
                        <s className="text-gray-500">{offerShown.copy.trialWas}</s> {offerShown.copy.trialNow}
                      </span>
                      <span className="sr-only">{offerShown.copy.trialSr}</span>
                    </p>
                    <p className="text-sm text-gray-600">{offerShown.copy.noCard}</p>
                  </div>
                )}

                {/* CTA Button */}
                {!isCurrentPlan && (
                  <div className="space-y-2">
                    {appMode && !isOwner ? (
                      <p className="text-sm text-gray-600">{t('plans:buttons.askOwner')}</p>
                    ) : (
                    <Button
                      onClick={() => handleChoosePlan(planCode)}
                      disabled={isPending || actionsHeld || (tooSmall && !signupMode && isAuthenticated)}
                      className="w-full"
                    >
                      {isPending || (actionsHeld && !statusKnown && !statusFailed) ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('plans:buttons.loading')}
                        </>
                      ) : ctaLabel}
                    </Button>
                    )}
                    {failures[planCode] && (
                      <div role="alert" className="flex items-start gap-2 text-sm text-red-700">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <p>{t(`plans:errors.${failures[planCode]}`)}</p>
                      </div>
                    )}
                    {showSeatMessage && (
                      <div role="alert" className="flex items-start gap-2 text-sm text-red-700">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <p>{t('subscription:billing.tooManyTeammates')}</p>
                          <p>{t('subscription:billing.removeTeammatesHint', { count: overBy })}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Features */}
                {planTranslation.features.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-900 mb-3">
                      {t('plans:pricing.includes')}
                    </p>
                    <ul className="space-y-2">
                      {planTranslation.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                          <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" strokeWidth={2.5} aria-hidden="true" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default PricingTable;
